/**
 * Main App Component
 * Root application component with routing and providers
 * 
 * FINLIST SSOT: unified category-envelope atomic wiring (global-scale perf target)
 */

import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { SidebarProvider, useSidebar } from '@/contexts/SidebarContext';
import { AppConfigProvider } from '@/contexts/AppConfigContext.jsx';
// FINLIST SSOT: Category-Envelope unified context for all pages
import { CategoryEnvelopeProvider } from '@/context/CategoryEnvelopeContext';
// FinTasks 2.0: Centralized task state management
import { FinTasksProvider } from '@/context/FinTasksContext';
// Phase 4: Gamification system
import { GamificationProvider } from '@/context/GamificationContext';
import { CelebrationOverlay } from '@/components/Gamification';
import ProtectedRoute, { PublicRoute } from '@/components/auth/ProtectedRoute';
import PremiumRoute from '@/components/auth/PremiumRoute';
import SignInPage from '@/pages/auth/SignInPage';
import SignUpPage from '@/pages/auth/SignUpPage';
import ForgotPasswordPage from '@/pages/auth/ForgotPasswordPage';
import ResetPasswordPage from '@/pages/auth/ResetPasswordPage';
import VerifyEmailPage from '@/pages/auth/VerifyEmailPage';
import OverviewPage from '@/pages/OverviewPage';
import ShoppingListPage from '@/pages/ShoppingListPage';
import ShoppingListsPage from '@/pages/ShoppingListsPage';
import SettingsPage from '@/pages/SettingsPage';
import HelpPage from '@/pages/HelpPage';
import ListHistoryPage from '@/pages/ListHistoryPage';
import SharedListViewPage from '@/pages/SharedListViewPage';
import ProfilePage from '@/pages/ProfilePage';
import NotFoundPage from '@/pages/NotFoundPage';
import PastItemsPage from '@/pages/PastItemsPage';
import PremiumOverviewPage from '@/pages/premium/OverviewPage';
import SubscribePage from '@/pages/SubscribePage';
import PremiumCategoriesPage from '@/pages/premium/CategoriesPage';
import FinanceOverviewPage from '@/pages/FinanceOverviewPage'; // FinList - Financial Overview
import PremiumBudgetsPage from '@/pages/premium/BudgetsPage'; // Premium Budgets feature
import TransactionsPage from '@/pages/premium/TransactionsPage'; // Premium Transactions feature
import TasksPage from '@/pages/TasksPage'; // FinTasks - To-Do Management
import TestNewComponents from '@/pages/TestNewComponents'; // Test page for Transaction 2.0 and Task 2.0
import WelcomePage from '@/pages/WelcomePage'; // Welcome / Landing Page
// Legal Pages - Public, no auth required
import TermsPage from '@/pages/legal/TermsPage';
import PrivacyPage from '@/pages/legal/PrivacyPage';
import CookiesPage from '@/pages/legal/CookiesPage';
import DPAPage from '@/pages/legal/DPAPage';
import CookieConsentBanner from '@/components/legal/CookieConsentBanner';
import PWAInstallPrompt from '@/components/PWAInstallPrompt';
import ConnectivityBadge from '@/components/dev/ConnectivityBadge.jsx';
// ThemeToggle removed from global layout - now only in Settings page
import PrimarySidebar from '@/components/navigation/PrimarySidebar.jsx';
import GlobalHomeLink from '@/components/navigation/GlobalHomeLink.jsx';
import PageTransition from '@/components/layout/PageTransition.jsx';
import { TooltipProvider } from '@/components/common/Tooltip.jsx';
// Feature 10: Offline/Sync Status Indicator
import { SyncStatusBanner } from '@/components/SyncStatusIndicator';
import { useEffect, useState, lazy, Suspense } from 'react';
import { registerServiceWorker, onSWMessage } from '@/services/pushService';
import { auth } from '@/config/firebase';
// FIX: Notification deep-linking support
import { resolveNotificationTarget } from '@/utils/notificationDeepLink';
// FINLIST AUTH RULES: Inactivity timeout hook
import { useInactivityTimeout } from '@/hooks/useInactivityTimeout';

// Debug logging - only in dev with VITE_DEBUG_APP=true
const DEBUG_APP = import.meta.env.DEV && import.meta.env.VITE_DEBUG_APP === 'true';

// Lazy-loaded heavy pages for bundle size optimization
const BudgetPage = lazy(() => import('@/pages/BudgetPage')); // Budget 2.0 - Advanced household budgeting
const GoalsPage = lazy(() => import('@/pages/premium/GoalsPage.jsx'));
const StatisticsPage = lazy(() => import('@/pages/StatisticsPage'));
const SavingsGoalsPage = lazy(() => import('@/pages/SavingsGoalsPage'));

/**
 * Loading fallback for lazy-loaded pages
 */
function PageLoadingFallback() {
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="flex flex-col items-center gap-3">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
        <span className="text-sm text-gray-500 dark:text-gray-400">Loading...</span>
      </div>
    </div>
  );
}

/**
 * Main Application Component
 */
function App() {
  const routerFutureFlags = {
    v7_startTransition: true,
    v7_relativeSplatPath: true
  };

  // Register service worker on app load
  useEffect(() => {
    const initServiceWorker = async () => {
      try {
        await registerServiceWorker();
        if (DEBUG_APP) console.log('[App] Service worker initialized');
      } catch (error) {
        console.error('[App] Service worker init failed:', error);
      }
    };

    initServiceWorker();

    // Listen for messages from service worker
    const unsubscribe = onSWMessage((message) => {
      if (DEBUG_APP) console.log('[App] Message from SW:', message);
      
      if (message.type === 'NOTIFICATION_CLICKED') {
        // FIX: Deep-link to the notification source using resolver
        if (DEBUG_APP) console.log('[App] Notification clicked, resolving target:', message.data);
        
        try {
          const target = resolveNotificationTarget(message.data);
          if (DEBUG_APP) console.log('[App] Navigating to:', target.path);
          
          // Use window.location for reliable navigation from outside Router context
          // This works whether the app is already open or freshly opened
          if (target.path && target.path !== window.location.pathname + window.location.search) {
            window.location.href = target.path;
          }
        } catch (error) {
          console.error('[App] Error resolving notification target:', error);
          // Fallback: navigate to home
          window.location.href = '/';
        }
      }
      
      if (message.type === 'PERIODIC_REMINDER_CHECK') {
        // Service worker requested a reminder check
        if (DEBUG_APP) console.log('[App] Periodic reminder check requested');
      }
    });

    return unsubscribe;
  }, []);

  const showConnectivityBadge = import.meta.env.VITE_SHOW_CONNECTIVITY_BADGE === 'true';
  return (
    <Router future={routerFutureFlags}>
      <ThemeProvider>
        <AuthProvider>
          {/* FINLIST SSOT: CategoryEnvelopeProvider provides unified category-envelope state */}
          <CategoryEnvelopeProvider>
            {/* FinTasks 2.0: Centralized task state management */}
            <FinTasksProvider>
              {/* Phase 4: Gamification system */}
              <GamificationProvider>
                <AppConfigProvider>
                  <SidebarProvider>
                    <TooltipProvider>
                      <AppLayout />
                      <CelebrationOverlay />
                    </TooltipProvider>
                  </SidebarProvider>
                </AppConfigProvider>
              </GamificationProvider>
            </FinTasksProvider>
          </CategoryEnvelopeProvider>
        </AuthProvider>
      </ThemeProvider>
    </Router>
  );
}

/**
 * Inactivity Monitor Component
 * FINLIST AUTH RULE 2: Auto sign-out after 30 minutes of inactivity
 * Runs the inactivity timeout hook to monitor user activity
 * Only active when user is authenticated
 */
function InactivityMonitor() {
  useInactivityTimeout();
  return null;
}

function AppLayout() {
  const { isOpen } = useSidebar();
  const location = useLocation();
  const { currentUser, loading } = useAuth();
  const showConnectivityBadge = false; // Disabled to prevent UI blocking

  // Public routes that should render without the app shell (sidebar, etc.)
  const shellFreeRoutes = ['/welcome', '/signin', '/signup', '/forgot-password', '/reset-password', '/terms', '/privacy', '/cookies', '/dpa'];
  
  // Check if this is a shell-free route OR root path for unauthenticated user
  const isShellFreeRoute = shellFreeRoutes.includes(location.pathname);
  const isUnauthenticatedRoot = location.pathname === '/' && !loading && !currentUser;

  // Render public pages without app shell (no sidebar, no GlobalHomeLink)
  if (isShellFreeRoute || isUnauthenticatedRoot) {
    return (
      <div className="App min-h-screen bg-background">
        <InactivityMonitor />
        <ScrollToTop />
        <AnimatedRoutes />
        <CookieConsentBanner />
      </div>
    );
  }

  // Show loading state while auth is being determined for root route
  if (location.pathname === '/' && loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500" />
      </div>
    );
  }

  return (
    <div className="App min-h-screen bg-background">
      {/* FINLIST AUTH RULE 2: Inactivity timeout monitor */}
      <InactivityMonitor />
      {/* Feature 10: Offline/Sync Status Banner */}
      <SyncStatusBanner />
      {/* {showConnectivityBadge && <ConnectivityBadge />} */}
      {/* ThemeToggle moved to Settings page only - was overlapping mobile nav */}
      <PrimarySidebar />
      <GlobalHomeLink />
      <motion.main
        className="relative min-h-screen overflow-y-auto"
        animate={{
          // 80 is 20rem which is 320px, the width of the sidebar
          paddingLeft: isOpen ? '320px' : '0px',
        }}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
      >
        <ScrollToTop />
        <AnimatedRoutes />
      </motion.main>
      {/* Global Toast Notifications - Hidden per user request */}
      {/* <Toaster position="top-right" /> */}
      {/* GDPR Cookie Consent Banner */}
      <CookieConsentBanner />
      {/* PWA Install Prompt - Shows on eligible devices */}
      <PWAInstallPrompt />
    </div>
  );
}

function ScrollToTop() {
  const location = useLocation();

  useEffect(() => {
    // Reset browser scroll
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    // Reset scroll on the main container if it's handling its own scroll
    const mainEl = document.querySelector('main');
    if (mainEl) {
      mainEl.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }
  }, [location.pathname]);

  return null;
}

function AnimatedRoutes() {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait" initial={false}>
      <Routes location={location} key={location.pathname}>
            {/* Welcome / Landing Page - Public */}
            <Route 
              path="/welcome" 
              element={
                <PageTransition><WelcomePage /></PageTransition>
              } 
            />

            {/* Public Routes - Redirect to dashboard if authenticated */}
            <Route 
              path="/signin" 
              element={
                <PublicRoute>
                  <PageTransition><SignInPage /></PageTransition>
                </PublicRoute>
              } 
            />
            <Route 
              path="/signup" 
              element={
                <PublicRoute>
                  <PageTransition><SignUpPage /></PageTransition>
                </PublicRoute>
              } 
            />
            <Route 
              path="/forgot-password" 
              element={
                <PublicRoute>
                  <PageTransition><ForgotPasswordPage /></PageTransition>
                </PublicRoute>
              } 
            />
            <Route 
              path="/reset-password" 
              element={
                <PublicRoute>
                  <PageTransition><ResetPasswordPage /></PageTransition>
                </PublicRoute>
              } 
            />
            <Route 
              path="/verify-email" 
              element={
                <ProtectedRoute>
                  <PageTransition><VerifyEmailPage /></PageTransition>
                </ProtectedRoute>
              } 
            />

            {/* Auth path redirects for backwards compatibility */}
            <Route path="/auth/signin" element={<Navigate to="/signin" replace />} />
            <Route path="/auth/signup" element={<Navigate to="/signup" replace />} />
            <Route path="/auth/forgot-password" element={<Navigate to="/forgot-password" replace />} />
            <Route path="/auth/reset-password" element={<Navigate to="/reset-password" replace />} />
            <Route path="/auth/verify-email" element={<Navigate to="/verify-email" replace />} />

            {/* Authenticated Routes */}
            <Route 
              path="/dashboard" 
              element={
                <PremiumRoute>
                  <PageTransition><OverviewPage /></PageTransition>
                </PremiumRoute>
              } 
            />
            <Route 
              path="/test-new" 
              element={
                <ProtectedRoute>
                  <PageTransition><TestNewComponents /></PageTransition>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/shopping-list/:listId" 
              element={
                <ProtectedRoute>
                  <PageTransition><ShoppingListPage /></PageTransition>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/shopping-lists" 
              element={
                <ProtectedRoute>
                  <PageTransition><ShoppingListsPage /></PageTransition>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/insights" 
              element={
                <Navigate to="/budget" replace /> // Insights merged into Budget
              } 
            />
            <Route 
              path="/savings-goals" 
              element={
                <PremiumRoute>
                  <Suspense fallback={<PageLoadingFallback />}>
                    <PageTransition><SavingsGoalsPage /></PageTransition>
                  </Suspense>
                </PremiumRoute>
              } 
            />
            <Route 
              path="/settings" 
              element={
                <ProtectedRoute>
                  <PageTransition><SettingsPage /></PageTransition>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/help" 
              element={
                <ProtectedRoute>
                  <PageTransition><HelpPage /></PageTransition>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/statistics" 
              element={
                <PremiumRoute>
                  <Suspense fallback={<PageLoadingFallback />}>
                    <PageTransition><StatisticsPage /></PageTransition>
                  </Suspense>
                </PremiumRoute>
              } 
            />
            <Route 
              path="/history" 
              element={
                <ProtectedRoute>
                  <PageTransition><ListHistoryPage /></PageTransition>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/past-items" 
              element={
                <ProtectedRoute>
                  <PageTransition><PastItemsPage /></PageTransition>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/profile" 
              element={
                <ProtectedRoute>
                  <PageTransition><ProfilePage /></PageTransition>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/budget" 
              element={
                <PremiumRoute>
                  <Suspense fallback={<PageLoadingFallback />}>
                    <PageTransition><BudgetPage /></PageTransition>
                  </Suspense>
                </PremiumRoute>
              } 
            />
            <Route 
              path="/finance" 
              element={
                <PremiumRoute>
                  <PageTransition><FinanceOverviewPage /></PageTransition>
                </PremiumRoute>
              } 
            />
            <Route 
              path="/insights/dashboard" 
              element={
                <Navigate to="/budget" replace /> // Insights merged into Budget
              } 
            />
            <Route 
              path="/tasks" 
              element={
                <PremiumRoute>
                  <PageTransition><TasksPage /></PageTransition>
                </PremiumRoute>
              } 
            />

            {/* Shared list view */}
            <Route path="/shared/:shareId" element={<SharedListViewPage />} />

            {/* Premium Routes */}
            <Route 
              path="/subscribe" 
              element={
                <ProtectedRoute>
                  <PageTransition><SubscribePage /></PageTransition>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/premium/overview" 
              element={
                <PremiumRoute>
                  <PageTransition><PremiumOverviewPage /></PageTransition>
                </PremiumRoute>
              } 
            />
            <Route 
              path="/premium/categories" 
              element={
                <PremiumRoute>
                  <PageTransition><PremiumCategoriesPage /></PageTransition>
                </PremiumRoute>
              } 
            />
            <Route 
              path="/premium/budgets" 
              element={
                <PremiumRoute>
                  <PageTransition><PremiumBudgetsPage /></PageTransition>
                </PremiumRoute>
              } 
            />
            <Route 
              path="/premium/transactions" 
              element={
                <PremiumRoute>
                  <PageTransition><TransactionsPage /></PageTransition>
                </PremiumRoute>
              } 
            />
            <Route 
              path="/premium/goals" 
              element={
                <PremiumRoute>
                  <Suspense fallback={<PageLoadingFallback />}>
                    <PageTransition><GoalsPage /></PageTransition>
                  </Suspense>
                </PremiumRoute>
              } 
            />

            {/* Default route - Welcome for unauthenticated, Dashboard for authenticated */}
            <Route 
              path="/" 
              element={<RootRoute />} 
            />

            {/* Legal Pages - Public, no auth required */}
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/cookies" element={<CookiesPage />} />
            <Route path="/dpa" element={<DPAPage />} />

            {/* 404 Not Found */}
            <Route path="*" element={<PageTransition><NotFoundPage /></PageTransition>} />
      </Routes>
    </AnimatePresence>
  );
}

/**
 * RootRoute - Conditional rendering based on auth state
 * Shows WelcomePage for unauthenticated users, redirects to dashboard for authenticated
 */
function RootRoute() {
  const { currentUser, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500" />
      </div>
    );
  }
  
  if (currentUser) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return <PageTransition><WelcomePage /></PageTransition>;
}

export default App;
